import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';

import AdLaunchService from 'services/AdLaunchService';
import { adUrl, adsetUrl } from './adsManager';
import { LaunchRow } from './types';

const STATUS_COLOR: Record<string, 'default' | 'success' | 'error' | 'info' | 'warning'> = {
  DONE: 'success',
  FAILED: 'error',
  PARTIAL: 'warning',
  RUNNING: 'info',
  PENDING: 'default',
};

// Worst-first, so a batch sorts by how much it needs looking at rather than
// alphabetically. Also the order the aggregate status is decided in.
const STATUS_RANK: Record<string, number> = {
  FAILED: 0,
  PARTIAL: 1,
  RUNNING: 2,
  PENDING: 3,
  DONE: 4,
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

type SortKey = 'created_at' | 'count' | 'product' | 'ad_type' | 'adset_name' | 'status';

interface Column {
  key: SortKey;
  label: string;
  nowrap?: boolean;
}

const COLUMNS: Column[] = [
  { key: 'created_at', label: 'When', nowrap: true },
  { key: 'count', label: 'Ads' },
  { key: 'product', label: 'Product' },
  { key: 'ad_type', label: 'Type' },
  { key: 'adset_name', label: 'Ad set', nowrap: true },
  { key: 'status', label: 'Status' },
];

/** One launch — every ad created by the same click shares a batch id. */
interface LaunchBatch {
  batchId: string;
  createdAt: string;
  rows: LaunchRow[];
  count: number;
  product: string;
  adType: string;
  adsetName: string;
  /** Only set when the whole batch landed in one ad set, which is the norm. */
  adsetId: string | null;
  campaignId: string | null;
  status: string;
  failed: number;
  pending: number;
  paused: number;
  untagged: number;
}

/** One value if the batch agrees on one, otherwise a count. */
function summarize(values: (string | null)[], plural: string): string {
  const distinct = Array.from(new Set(values.filter(Boolean) as string[]));
  if (distinct.length === 0) return '—';
  if (distinct.length === 1) return distinct[0];
  return `${distinct.length} ${plural}`;
}

function onlyValue(values: (string | null)[]): string | null {
  const distinct = Array.from(new Set(values.filter(Boolean) as string[]));
  return distinct.length === 1 ? distinct[0] : null;
}

function batchStatus(rows: LaunchRow[]): string {
  if (rows.some(r => r.status === 'RUNNING' || r.status === 'PENDING')) return 'RUNNING';
  const failed = rows.filter(r => r.status === 'FAILED').length;
  if (failed === 0) return 'DONE';
  return failed === rows.length ? 'FAILED' : 'PARTIAL';
}

/**
 * Ledger rows come back one per ad. The table shows one row per launch, since
 * a launch is what an operator did and what they go looking for; the ads it
 * created are inside it.
 */
function groupBatches(rows: LaunchRow[]): LaunchBatch[] {
  const byBatch = new Map<string, LaunchRow[]>();
  rows.forEach(row => {
    const existing = byBatch.get(row.batch_id);
    if (existing) existing.push(row);
    else byBatch.set(row.batch_id, [row]);
  });

  return Array.from(byBatch.entries()).map(([batchId, batchRows]) => {
    const ordered = [...batchRows].sort((a, b) => a.created_at.localeCompare(b.created_at));
    return {
      batchId,
      // The batch happened when its first ad was queued; the rest follow it by
      // however long Meta took.
      createdAt: ordered[0].created_at,
      rows: ordered,
      count: ordered.length,
      product: summarize(ordered.map(r => r.product), 'products'),
      adType: summarize(ordered.map(r => r.ad_type), 'types'),
      adsetName: summarize(ordered.map(r => r.adset_name), 'ad sets'),
      adsetId: onlyValue(ordered.map(r => r.adset_id)),
      campaignId: onlyValue(ordered.map(r => r.campaign_id)),
      status: batchStatus(ordered),
      failed: ordered.filter(r => r.status === 'FAILED').length,
      pending: ordered.filter(r => r.status === 'PENDING' || r.status === 'RUNNING').length,
      paused: ordered.filter(r => r.ad_status === 'PAUSED' && r.status === 'DONE').length,
      untagged: ordered.filter(r => r.status === 'DONE' && !r.notion_marked).length,
    };
  });
}

function sortValue(batch: LaunchBatch, key: SortKey): string | number {
  switch (key) {
    case 'created_at': return batch.createdAt;
    case 'count': return batch.count;
    case 'product': return batch.product;
    case 'ad_type': return batch.adType;
    case 'adset_name': return batch.adsetName;
    case 'status': return STATUS_RANK[batch.status] ?? STATUS_RANK.DONE;
    default: return '';
  }
}

function AdStatusChips({ row }: { row: LaunchRow }) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Tooltip title={row.error_message || ''}>
        <Chip size="small" color={STATUS_COLOR[row.status] ?? 'default'} label={row.status} />
      </Tooltip>
      {row.ad_status === 'PAUSED' && row.status === 'DONE' && (
        <Chip size="small" variant="outlined" label="paused" />
      )}
      {row.status === 'DONE' && !row.notion_marked && (
        <Tooltip title="The ad was created but its Notion row could not be tagged Launched — tag it by hand, or it will be offered again.">
          <Chip size="small" color="warning" variant="outlined" label="untagged" />
        </Tooltip>
      )}
    </Stack>
  );
}

function BatchRow({
  batch,
  accountId,
  expanded,
  onToggle,
}: {
  batch: LaunchBatch;
  accountId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const first = batch.rows[0];
  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: expanded ? 'unset' : undefined }, cursor: 'pointer' }} onClick={onToggle}>
        <TableCell padding="checkbox">
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse launch' : 'Expand launch'}
            onClick={e => { e.stopPropagation(); onToggle(); }}
          >
            {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(batch.createdAt).toLocaleString()}</TableCell>
        <TableCell>
          <Typography variant="body2">{batch.count}</Typography>
          <Typography variant="caption" color="text.secondary">
            {first.creative_name}{batch.count > 1 ? ` +${batch.count - 1}` : ''}
          </Typography>
        </TableCell>
        <TableCell>{batch.product}</TableCell>
        <TableCell>{batch.adType}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {/* One ad set for the batch is the normal case, and its link is the
              one worth having: it opens the whole launch together. */}
          {batch.adsetId ? (
            <Link
              href={adsetUrl(accountId, batch.adsetId, batch.campaignId)}
              target="_blank"
              rel="noopener"
              onClick={e => e.stopPropagation()}
            >
              {batch.adsetName}
            </Link>
          ) : batch.adsetName}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" color={STATUS_COLOR[batch.status] ?? 'default'} label={batch.status} />
            {batch.failed > 0 && batch.status === 'PARTIAL' && (
              <Chip size="small" variant="outlined" color="error" label={`${batch.failed} failed`} />
            )}
            {batch.pending > 0 && (
              <Chip size="small" variant="outlined" label={`${batch.pending} in flight`} />
            )}
            {batch.paused > 0 && <Chip size="small" variant="outlined" label={`${batch.paused} paused`} />}
            {batch.untagged > 0 && (
              <Tooltip title="Created, but the Notion row could not be tagged Launched — tag it by hand, or it will be offered again.">
                <Chip size="small" variant="outlined" color="warning" label={`${batch.untagged} untagged`} />
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }} colSpan={COLUMNS.length + 1}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1, ml: 4 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Creative</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Ad set</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Ad</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batch.rows.map(row => (
                    <TableRow key={row.launch_id}>
                      <TableCell>{row.creative_name}</TableCell>
                      <TableCell>{row.product}</TableCell>
                      <TableCell>{row.ad_type}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.adset_name}</TableCell>
                      <TableCell><AdStatusChips row={row} /></TableCell>
                      {/* Ad set and campaign are selected alongside the ad, so
                          the link lands with its batch in view rather than on
                          an ad floating by itself. */}
                      <TableCell>
                        {row.meta_ad_id ? (
                          <Link
                            href={adUrl(accountId, row.meta_ad_id, { campaignId: row.campaign_id, adsetId: row.adset_id })}
                            target="_blank"
                            rel="noopener"
                          >
                            {row.meta_ad_id}
                          </Link>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function HistoryTab() {
  const service = useMemo(() => new AdLaunchService(), []);
  const [rows, setRows] = useState<LaunchRow[] | null>(null);
  const [accountId, setAccountId] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDesc, setSortDesc] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    const res = await service.getLaunches(200);
    setRows(res?.launches ?? []);
    // Comes back with the launches so the Ads Manager links can name the
    // account, rather than a second round trip just for that.
    setAccountId(res?.accountId ?? '');
    setLoading(false);
  }, [service]);

  useEffect(() => { load(); }, [load]);

  const batches = useMemo(() => {
    const grouped = groupBatches(rows ?? []);
    const direction = sortDesc ? -1 : 1;
    return grouped.sort((a, b) => {
      const left = sortValue(a, sortKey);
      const right = sortValue(b, sortKey);
      if (left === right) return b.createdAt.localeCompare(a.createdAt);
      return (left > right ? 1 : -1) * direction;
    });
  }, [rows, sortKey, sortDesc]);

  const sortBy = (key: SortKey) => {
    // A new column starts newest/worst-first, which is what the ranks encode;
    // clicking the current one flips it.
    if (key === sortKey) setSortDesc(!sortDesc);
    else { setSortKey(key); setSortDesc(key === 'created_at' || key === 'count'); }
    setPage(0);
  };

  const toggleExpanded = (batchId: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(batchId)) next.delete(batchId);
    else next.add(batchId);
    return next;
  });

  const visible = batches.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading && !rows) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2, gap: 2 }}>
        <Typography variant="h6">Launch history</Typography>
        {batches.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            {batches.length} launches · {rows?.length ?? 0} ads
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Stack>

      {batches.length === 0 && <Alert severity="info">Nothing has been launched from here yet.</Alert>}

      {batches.length > 0 && (
        <>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {COLUMNS.map(col => (
                    <TableCell key={col.key} sx={col.nowrap ? { whiteSpace: 'nowrap' } : undefined}>
                      <TableSortLabel
                        active={sortKey === col.key}
                        direction={sortKey === col.key && sortDesc ? 'desc' : 'asc'}
                        onClick={() => sortBy(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map(batch => (
                  <BatchRow
                    key={batch.batchId}
                    batch={batch}
                    accountId={accountId}
                    expanded={expanded.has(batch.batchId)}
                    onToggle={() => toggleExpanded(batch.batchId)}
                  />
                ))}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            component="div"
            count={batches.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            labelRowsPerPage="Launches per page"
          />
        </>
      )}
    </Box>
  );
}
